<template>
    <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
      <HrSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
    <div class="max-w-8xl mx-auto  mt-4 mb-4 mx-auto p-6 bg-white rounded-lg shadow-md container">
        <!-- Summary Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <!-- Total Staff on Leave -->
      <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#fae240]">
        <div class="flex justify-between items-center">
          <div>
            <p class="text-sm font-medium text-[#035a76]">Total Staff on Leave</p>
            <p class="text-2xl font-bold text-[#035a76] mt-1">{{ totalStaffOnLeave }}</p>
          </div>
          <div class="p-3 rounded-full bg-blue-100 text-[#035a76]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>
        <p class="text-xs text-[#035a76] mt-2">All staff currently on leave</p>
      </div>

      <!-- Staff on Leave by Section -->
      <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#e8037e]">
        <div class="flex justify-between items-center">
          <div>
            <p class="text-sm font-medium text-[#035a76]">Teaching Staff on Leave</p>
            <p class="text-2xl font-bold text-[#035a76] mt-1">{{ teachingStaffOnLeave }}</p>
            <div class="flex space-x-2 mt-1">
              <span class="text-xs bg-[#fae240] text-[#035a76] px-2 py-1 rounded">N: {{ staffOnLeaveBySection.nursery }}</span>
              <span class="text-xs bg-[#e8037e] text-[#035a76] px-2 py-1 rounded">P: {{ staffOnLeaveBySection.primary }}</span>
              <span class="text-xs bg-[#76ba51] text-[#035a76] px-2 py-1 rounded">S: {{ staffOnLeaveBySection.secondary }}</span>
            </div>
          </div>
          <div class="p-3 rounded-full bg-blue-100 text-[#035a76]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        </div>
        <p class="text-xs text-[#035a76] mt-2">N=Nursery, P=Primary, S=Secondary</p>
      </div>

      <!-- Non-Teaching Staff on Leave -->
      <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#76ba51]">
        <div class="flex justify-between items-center">
          <div>
            <p class="text-sm font-medium text-[#035a76]">Non-Teaching Staff on Leave</p>
            <p class="text-2xl font-bold text-[#035a76] mt-1">{{ nonTeachingStaffOnLeave }}</p>
          </div>
          <div class="p-3 rounded-full bg-blue-100 text-[#035a76]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
        <p class="text-xs text-[#035a76] mt-2">Administrative and support staff</p>
      </div>
    </div>

      <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-[#035a76]">Leave Management</h1>
        <div class="flex space-x-4">
          <button @click="openNewLeaveModal" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90">
            + New Leave Request
          </button>
        </div>
      </div>
  
      <!-- Employee Search -->
      <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6 mb-8 bg-gray-50 p-4 rounded-md">
        <h3 class="text-lg font-medium mb-3">Find Employee</h3>
        <div class="relative">
          <input 
            v-model="employeeSearch" 
            @input="searchEmployees" 
            type="text" 
            placeholder="Search by Staff ID or Name" 
            class="w-full p-3 border border-gray-300 rounded-md focus:ring-[#035a76] focus:border-[#035a76]"
          >
          <ul v-if="searchResults.length > 0 && employeeSearch" class="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            <li 
              v-for="employee in searchResults" 
              :key="employee.id" 
              @click="selectEmployee(employee)"
              class="p-3 hover:bg-gray-100 cursor-pointer"
            >
              <div class="flex justify-between">
                <span class="font-medium">{{ employee.name }}</span>
                <span class="text-gray-500 text-sm">{{ employee.position }}</span>
              </div>
              <div class="text-sm text-gray-500">ID: {{ employee.id }}</div>
            </li>
          </ul>
        </div>
  
        <!-- Employee Details -->
        <div v-if="selectedEmployee" class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Employee Name</p>
            <p class="font-medium">{{ selectedEmployee.name }}</p>
          </div>
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Position</p>
            <p class="font-medium">{{ selectedEmployee.position }}</p>
          </div>
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Department</p>
            <p class="font-medium">{{ selectedEmployee.department }}</p>
          </div>
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Staff ID</p>
            <p class="font-medium">{{ selectedEmployee.id }}</p>
          </div>
        </div>
      </div>
  
      <!-- Leave Balance Summary -->
      <div v-if="selectedEmployee" class="mb-8">
        <h3 class="text-lg font-medium mb-3">Leave Balance</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div v-for="(balance, type) in leaveBalances" :key="type" class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500">{{ type }} Leave</p>
                <p class="text-2xl font-bold">{{ balance.remaining }}/{{ balance.total }}</p>
              </div>
              <span class="px-2 py-1 text-xs rounded-full" 
                    :class="balance.remaining > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                {{ balance.remaining > 0 ? 'Available' : 'Exhausted' }}
              </span>
            </div>
            <div class="mt-2 text-xs text-gray-500">
              Last taken: {{ balance.lastTaken || 'Never' }}
            </div>
          </div>
        </div>
      </div>
  
      <!-- Leave History -->
      <div v-if="selectedEmployee" class="mb-8">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium">Leave History</h3>
          <div class="flex space-x-2">
            <select v-model="leaveStatusFilter" class="text-sm border rounded px-3 py-1 focus:ring-[#035a76] focus:border-[#035a76]">
              <option value="all">All Statuses</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
            <select v-model="leaveTypeFilter" class="text-sm border rounded px-3 py-1 focus:ring-[#035a76] focus:border-[#035a76]">
              <option value="all">All Types</option>
              <option v-for="type in leaveTypes" :value="type.value">{{ type.label }}</option>
            </select>
          </div>
        </div>
  
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approver</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="request in filteredLeaveHistory" :key="request.id">
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 py-1 text-xs rounded-full" :style="{ backgroundColor: getLeaveTypeColor(request.type), color: 'white' }">
                    {{ request.type }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ formatDate(request.startDate) }} - {{ formatDate(request.endDate) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  {{ request.duration }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 py-1 text-xs rounded-full" :class="{
                    'bg-yellow-100 text-yellow-800': request.status === 'pending',
                    'bg-green-100 text-green-800': request.status === 'approved',
                    'bg-red-100 text-red-800': request.status === 'rejected'
                  }">
                    {{ request.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ request.approver || 'N/A' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button @click="viewLeaveDetails(request)" class="text-[#035a76] hover:text-[#035a76]/70 mr-2">
                    View
                  </button>
                  <button v-if="request.status === 'pending'" @click="cancelLeaveRequest(request.id)" class="text-red-600 hover:text-red-900">
                    Cancel
                  </button>
                </td>
              </tr>
              <tr v-if="filteredLeaveHistory.length === 0">
                <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">
                  No leave requests found
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
  
      <!-- New Leave Modal -->
      <div v-if="showNewLeaveModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div class="px-6 py-4 border-b">
            <h3 class="text-lg font-medium text-gray-900">New Leave Request</h3>
          </div>
          <div class="px-6 py-4">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Leave Type</label>
                <select v-model="newLeave.type" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                  <option v-for="type in leaveTypes" :value="type.value">{{ type.label }}</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700">Date Range</label>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                  <div>
                    <label class="block text-xs text-gray-500">Start Date</label>
                    <input v-model="newLeave.startDate" type="date" required 
                           class="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500">End Date</label>
                    <input v-model="newLeave.endDate" type="date" required 
                           class="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                  </div>
                </div>
              </div>
  
              <div>
                <label class="block text-sm font-medium text-gray-700">Duration</label>
                <input v-model="newLeave.duration" type="text" readonly 
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 focus:outline-none">
              </div>
  
              <div>
                <label class="block text-sm font-medium text-gray-700">Reason</label>
                <textarea v-model="newLeave.reason" rows="3" required
                          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                          placeholder="Enter reason for leave..."></textarea>
              </div>
  
              <div>
                <label class="block text-sm font-medium text-gray-700">Attachment (Optional)</label>
                <input type="file" @change="handleFileUpload" 
                       class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20">
              </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t flex justify-end space-x-3">
            <button @click="showNewLeaveModal = false" type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
              Cancel
            </button>
            <button @click="submitLeaveRequest" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
              Submit Request
            </button>
          </div>
        </div>
      </div>
  
      <!-- Leave Details Modal -->
      <div v-if="selectedLeave" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div class="px-6 py-4 border-b flex justify-between items-center">
            <h3 class="text-lg font-medium text-gray-900">Leave Details - {{ selectedLeave.type }}</h3>
            <button @click="selectedLeave = null" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-6 py-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="space-y-4">
                <div>
                  <p class="text-sm text-gray-500">Employee</p>
                  <p class="font-medium">{{ selectedLeave.employeeName }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-500">Dates</p>
                  <p class="font-medium">{{ formatDate(selectedLeave.startDate) }} to {{ formatDate(selectedLeave.endDate) }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-500">Duration</p>
                  <p class="font-medium">{{ selectedLeave.duration }} days</p>
                </div>
              </div>
              <div class="space-y-4">
                <div>
                  <p class="text-sm text-gray-500">Status</p>
                  <p>
                    <span class="px-2 py-1 text-xs rounded-full" :class="{
                      'bg-yellow-100 text-yellow-800': selectedLeave.status === 'pending',
                      'bg-green-100 text-green-800': selectedLeave.status === 'approved',
                      'bg-red-100 text-red-800': selectedLeave.status === 'rejected'
                    }">
                      {{ selectedLeave.status }}
                    </span>
                  </p>
                </div>
                <div>
                  <p class="text-sm text-gray-500">Submitted On</p>
                  <p class="font-medium">{{ formatDateTime(selectedLeave.submittedOn) }}</p>
                </div>
                <div v-if="selectedLeave.approver">
                  <p class="text-sm text-gray-500">Approved By</p>
                  <p class="font-medium">{{ selectedLeave.approver }}</p>
                </div>
              </div>
            </div>
            
            <div class="mt-6">
              <p class="text-sm text-gray-500">Reason</p>
              <p class="mt-1 p-3 bg-gray-50 rounded">{{ selectedLeave.reason }}</p>
            </div>
            
            <div v-if="selectedLeave.notes" class="mt-4">
              <p class="text-sm text-gray-500">Approver Notes</p>
              <p class="mt-1 p-3 bg-gray-50 rounded">{{ selectedLeave.notes }}</p>
            </div>
            
            <div v-if="selectedLeave.attachment" class="mt-4">
              <p class="text-sm text-gray-500">Attachment</p>
              <a href="#" @click.prevent="downloadAttachment" class="mt-1 inline-flex items-center text-[#035a76] hover:underline">
                <svg class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                {{ selectedLeave.attachment.name }}
              </a>
            </div>
          </div>
          <div v-if="isManager && selectedLeave.status === 'pending'" class="px-6 py-4 border-t flex justify-end space-x-3">
            <button @click="approveLeave(selectedLeave.id)" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
              Approve
            </button>
            <button @click="rejectLeave(selectedLeave.id)" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
    </main>
    </div>
  </template>
  
  <script setup>
  
import { ref, computed, watch } from 'vue';
import HrSidebar from '../../components/hr/HrSidebar.vue';
  import HrTopbar from '../../components/hr/HrTopbar.vue';
  
  const isSidebarOpen = ref(true);
  const isDarkMode = ref(false);
  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };

// Existing sample data
const sampleEmployees = [
  { id: 'EMP001', name: 'Kwame Mensah', position: 'Mathematics Teacher', department: 'Academics', section: 'secondary' },
  { id: 'EMP002', name: 'Ama Asante', position: 'Science Teacher', department: 'Academics', section: 'primary' },
  { id: 'EMP003', name: 'Yaw Boateng', position: 'HR Manager', department: 'Administration', section: 'non-teaching' },
  { id: 'EMP004', name: 'Esi Coleman', position: 'Finance Officer', department: 'Administration', section: 'non-teaching' },
  { id: 'EMP005', name: 'Akosua Nkrumah', position: 'Nursery Teacher', department: 'Academics', section: 'nursery' },
];

const sampleLeaveHistory = [
  { id: 1, employeeName: 'Kwame Mensah', type: 'Annual', startDate: '2025-04-10', endDate: '2025-04-15', duration: 5, status: 'approved', reason: 'Family vacation', approver: 'Yaw Boateng', submittedOn: '2025-03-20T09:30:00', notes: 'Approved as per policy', attachment: { name: 'travel_plans.pdf' }, section: 'secondary' },
  { id: 2, employeeName: 'Ama Asante', type: 'Sick', startDate: '2025-04-11', endDate: '2025-04-13', duration: 3, status: 'approved', reason: 'Medical appointment', approver: 'Yaw Boateng', submittedOn: '2025-04-10T14:20:00', notes: 'Medical certificate provided', attachment: null, section: 'primary' },
  { id: 3, employeeName: 'Akosua Nkrumah', type: 'Annual', startDate: '2025-04-11', endDate: '2025-04-12', duration: 2, status: 'pending', reason: 'Personal reasons', approver: null, submittedOn: '2025-04-10T11:10:00', notes: null, attachment: null, section: 'nursery' },
  { id: 4, employeeName: 'Yaw Boateng', type: 'Annual', startDate: '2025-04-11', endDate: '2025-04-14', duration: 4, status: 'approved', reason: 'Family event', approver: 'Esi Coleman', submittedOn: '2025-04-01T11:10:00', notes: null, attachment: null, section: 'non-teaching' },
];

// New sample data for summary
const totalStaffOnLeave = ref(4); // Hardcoded for frontend demo
const teachingStaffOnLeave = ref(3);
const nonTeachingStaffOnLeave = ref(1);
const staffOnLeaveBySection = ref({
  nursery: 1,
  primary: 1,
  secondary: 1,
});

// Existing reactive state
const employeeSearch = ref('');
const searchResults = ref([]);
const selectedEmployee = ref(null);
const leaveBalances = ref({});
const leaveStatusFilter = ref('all');
const leaveTypeFilter = ref('all');
const showNewLeaveModal = ref(false);
const selectedLeave = ref(null);
const isManager = ref(true);

const leaveTypes = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'maternity', label: 'Maternity Leave' },
  { value: 'paternity', label: 'Paternity Leave' },
  { value: 'study', label: 'Study Leave' },
  { value: 'compassionate', label: 'Compassionate Leave' },
];

const newLeave = ref({
  type: 'annual',
  startDate: '',
  endDate: '',
  duration: 0,
  reason: '',
  attachment: null
});

// Computed properties
const filteredLeaveHistory = computed(() => {
  let filtered = sampleLeaveHistory.filter(leave => leave.employeeName === selectedEmployee.value?.name);
  
  if (leaveStatusFilter.value !== 'all') {
    filtered = filtered.filter(leave => leave.status === leaveStatusFilter.value);
  }
  
  if (leaveTypeFilter.value !== 'all') {
    filtered = filtered.filter(leave => leave.type.toLowerCase() === leaveTypeFilter.value);
  }
  
  return filtered;
});

// Methods (unchanged from original except for new additions)
const searchEmployees = () => {
  if (employeeSearch.value.length < 2) {
    searchResults.value = [];
    return;
  }
  
  const searchTerm = employeeSearch.value.toLowerCase();
  searchResults.value = sampleEmployees.filter(emp => 
    emp.name.toLowerCase().includes(searchTerm) || 
    emp.id.toLowerCase().includes(searchTerm)
  );
};

const selectEmployee = (employee) => {
  selectedEmployee.value = employee;
  employeeSearch.value = employee.name;
  searchResults.value = [];
  leaveBalances.value = sampleLeaveBalances;
};

const openNewLeaveModal = () => {
  if (!selectedEmployee.value) {
    alert('Please select an employee first');
    return;
  }
  
  const today = new Date();
  newLeave.value.startDate = today.toISOString().split('T')[0];
  newLeave.value.endDate = today.toISOString().split('T')[0];
  newLeave.value.duration = 1;
  showNewLeaveModal.value = true;
};

const calculateDuration = () => {
  if (newLeave.value.startDate && newLeave.value.endDate) {
    const start = new Date(newLeave.value.startDate);
    const end = new Date(newLeave.value.endDate);
    const diffTime = Math.abs(end - start);
    newLeave.value.duration = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }
};

watch(() => [newLeave.value.startDate, newLeave.value.endDate], () => {
  calculateDuration();
});

const submitLeaveRequest = () => {
  alert(`Leave request submitted for ${selectedEmployee.value.name}`);
  showNewLeaveModal.value = false;
};

const viewLeaveDetails = (leave) => {
  selectedLeave.value = leave;
};

const approveLeave = (id) => {
  const leave = sampleLeaveHistory.find(l => l.id === id);
  if (leave) {
    leave.status = 'approved';
    leave.approver = 'Current User';
    leave.notes = 'Approved by manager';
  }
  selectedLeave.value = null;
};

const rejectLeave = (id) => {
  const leave = sampleLeaveHistory.find(l => l.id === id);
  if (leave) {
    leave.status = 'rejected';
    leave.approver = 'Current User';
    leave.notes = 'Rejected due to insufficient notice';
  }
  selectedLeave.value = null;
};

const cancelLeaveRequest = (id) => {
  const index = sampleLeaveHistory.findIndex(l => l.id === id);
  if (index !== -1) {
    sampleLeaveHistory.splice(index, 1);
  }
};

const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    newLeave.value.attachment = {
      name: file.name,
      file: file
    };
  }
};

const downloadAttachment = () => {
  alert(`Downloading ${selectedLeave.value.attachment.name}`);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getLeaveTypeColor = (type) => {
  const colors = {
    annual: '#4CAF50',
    sick: '#FF9800',
    maternity: '#9C27B0',
    paternity: '#2196F3',
    study: '#009688',
    compassionate: '#607D8B'
  };
  return colors[type.toLowerCase()] || '#9E9E9E';
};

// Sample leave balances (unchanged)
const sampleLeaveBalances = {
  annual: { total: 25, remaining: 15, lastTaken: '2023-03-15' },
  sick: { total: 10, remaining: 8, lastTaken: '2023-05-20' },
  maternity: { total: 90, remaining: 90, lastTaken: null },
  paternity: { total: 10, remaining: 10, lastTaken: null },
};
</script>
  
  <style scoped>
  /* Additional custom styles can go here if needed */
  </style>